/**
 * Outils › Messagerie (outils.messagerie) – règles partagées API / front de la messagerie Microsoft 365 allégée
 * (lot 1 du 27/10/2026) : boîte de l'utilisateur lue et écrite par Microsoft Graph avec SES droits délégués, via l'API
 * AGORA (jeton Entra conservé chiffré côté AWS). Détail : docs/MESSAGERIE.md.
 */
import { nettoyerHtmlRedaction } from './html-redaction';

export const RESSOURCE_MESSAGERIE = 'outils.messagerie';

/**
 * Dossiers connus de Microsoft Graph (noms « well-known ») : utilisables à la place d'un identifiant de dossier et
 * reconnus dans la liste des dossiers (icône, ordre, libellé traduit).
 */
export const DOSSIERS_CONNUS = ['inbox', 'drafts', 'sentitems', 'archive', 'deleteditems', 'junkemail'] as const;
export type DossierConnu = (typeof DOSSIERS_CONNUS)[number];
export const DOSSIER_PAR_DEFAUT: DossierConnu = 'inbox';

/** Filtres rapides de la liste (un seul à la fois, dans l'adresse de la page). */
export const FILTRES_MESSAGES = ['tous', 'nonLus', 'drapeau', 'piecesJointes', 'importants'] as const;
export type FiltreMessages = (typeof FILTRES_MESSAGES)[number];

/** Statut de suivi (drapeau Outlook) : notFlagged / flagged / complete. */
export const DRAPEAUX = ['aucun', 'suivi', 'termine'] as const;
export type Drapeau = (typeof DRAPEAUX)[number];
export const IMPORTANCES = ['faible', 'normale', 'haute'] as const;
export type Importance = (typeof IMPORTANCES)[number];

/** Réponse à un message : répondre (expéditeur), répondre à tous, transférer. */
export const MODES_REPONSE = ['repondre', 'repondreTous', 'transferer'] as const;
export type ModeReponse = (typeof MODES_REPONSE)[number];

/** Couleurs prédéfinies des catégories Outlook (preset0…preset24) ; `none` = sans couleur. */
export const COULEURS_CATEGORIE = ['none', ...Array.from({ length: 25 }, (_, i) => `preset${i}`)] as const;

/** Bornes : une page de liste, destinataires d'un message, catégories d'un message, taille d'un texte saisi. */
export const LIMITE_MESSAGES = 100;
export const MAX_DESTINATAIRES = 100;
export const MAX_CATEGORIES = 25;
export const MAX_CORPS = 100_000;
/**
 * Répartition de l'écran entre la liste des messages et le message ouvert : part de la liste en % (préférence
 * messagerieRepartition), réglée en faisant glisser la séparation ou dans les Réglages (préréglages).
 */
export const REPARTITION_MESSAGERIE = { min: 25, max: 75, defaut: 42, pas: 5 } as const;
export const PREREGLAGES_REPARTITION = { lecture: 30, equilibre: 42, liste: 58 } as const;

/** Profondeur maximale des dossiers parcourus dans AGORA (au-delà : dans Outlook). */
export const NIVEAU_MAX_DOSSIERS = 15;

/**
 * Pièce jointe téléchargée par l'API (réponse JSON en base64, sous la limite de 6 Mo d'une réponse Lambda) ; au-delà,
 * le message s'ouvre dans Outlook sur le web.
 */
export const TAILLE_MAX_TELECHARGEMENT = 4 * 1024 * 1024;
/** Images intégrées (cid:) remplacées dans le corps HTML : au-delà de ce total, elles restent masquées. */
export const TAILLE_MAX_IMAGES_INTEGREES = 3 * 1024 * 1024;

/**
 * Droits délégués demandés à Entra pour l'utilisateur (application « AGORA Messagerie ») : lecture et écriture de SA
 * boîte, envoi en SON nom, lecture de ses catégories ; offline_access pour le jeton de rafraîchissement.
 */
export const PORTEES_GRAPH = [
  'openid', 'profile', 'email', 'offline_access',
  'https://graph.microsoft.com/User.Read',
  'https://graph.microsoft.com/Mail.ReadWrite',
  'https://graph.microsoft.com/Mail.Send',
  'https://graph.microsoft.com/MailboxSettings.Read',
] as const;

/**
 * Droits délégués FACULTATIFS, demandés en plus de PORTEES_GRAPH : lecture de l'agenda de l'utilisateur (Scribe, réunion
 * Outlook en cours proposée au démarrage, 02/11/2026). Consentement d'administrateur absent pour ce droit : la connexion
 * se renouvelle sans lui (api/src/outils/messagerie/jetons.ts) et Scribe ne propose rien.
 */
export const PORTEES_GRAPH_FACULTATIVES = ['https://graph.microsoft.com/Calendars.Read'] as const;

/** Adresse du front où Entra renvoie l'utilisateur après la connexion de sa messagerie (écran de la messagerie). */
export const ROUTE_MESSAGERIE = '/outils/messagerie';

/** Police des e-mails rédigés dans AGORA (comme Outlook). */
const ENVELOPPE_CORPS = 'font-family: Calibri, Arial, sans-serif; font-size: 11pt';

/**
 * Corps saisi en texte → HTML envoyé à Graph : caractères spéciaux échappés, sauts de ligne conservés. Même conversion
 * pour l'API (envoi) et la maquette.
 */
export function texteVersHtml(texte: string): string {
  const echappe = texte
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  return `<div style="${ENVELOPPE_CORPS}">${echappe.replace(/\r?\n/g, '<br>')}</div>`;
}

/** Format du corps saisi : texte simple, ou HTML de l'éditeur (mise en forme de base, nettoyé : html-redaction.ts). */
export const FORMATS_CORPS = ['texte', 'html'] as const;
export type FormatCorps = (typeof FORMATS_CORPS)[number];

/**
 * Corps saisi → HTML envoyé à Graph (API et maquette) : le HTML de l'éditeur est TOUJOURS nettoyé ici. `signature` :
 * signature PRODEVAL déjà rendue (rendreSignature), placée sous le texte après une ligne vide. `origineImages` : adresse
 * du front, préfixée aux images AGORA (/signature/…) pour que le destinataire les charge.
 */
export function corpsEnHtml(corps: string, format: FormatCorps = 'texte', signature?: string | null, origineImages?: string | null): string {
  const texte = format === 'html' ? `<div style="${ENVELOPPE_CORPS}">${nettoyerHtmlRedaction(corps, origineImages)}</div>` : texteVersHtml(corps);
  return signature ? `${texte}<div style="${ENVELOPPE_CORPS}"><br>${nettoyerHtmlRedaction(signature, origineImages)}</div>` : texte;
}

/**
 * Signatures e-mail PRODEVAL (rédigées par le marketing dans AGORA, affectées à des groupes) : HTML de la rédaction
 * (styles en ligne, images AGORA), avec des variables {{…}} remplacées par les données de l'utilisateur (profil
 * Microsoft 365, sinon AGORA).
 */
export const VARIABLES_SIGNATURE = ['prenom', 'nom', 'nomComplet', 'fonction', 'service', 'telephone', 'mobile', 'email'] as const;
export type VariableSignature = (typeof VARIABLES_SIGNATURE)[number];
export const MAX_SIGNATURE = 20_000;

const echapperValeur = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const VARIABLE = /\{\{\s*([a-zA-Z]+)\s*\}\}/g;
const estVariable = (v: string): v is VariableSignature => (VARIABLES_SIGNATURE as readonly string[]).includes(v);
/** Séparateurs de lignes d'une signature nettoyée : retour à la ligne, balises de bloc (avec leur style éventuel). */
const LIGNES_SIGNATURE = /(<br>|<\/?(?:p|div|li|ul|ol|blockquote|table|tbody|tr|td)(?:\s[^>]*)?>)/;

/**
 * Signature → HTML envoyé (API, maquette, aperçus) : contenu nettoyé, variables remplacées par les valeurs échappées ;
 * une LIGNE dont une variable connue est vide disparaît (« Mobile : {{mobile}} » sans mobile), avec son retour à la
 * ligne ; une variable inconnue reste telle quelle (visible dans l'aperçu de l'administration).
 */
export function rendreSignature(
  contenu: string, valeurs: Partial<Record<VariableSignature, string | null>>, origineImages?: string | null,
): string {
  const sortie: string[] = [];
  let sauterRetour = false;
  for (const morceau of nettoyerHtmlRedaction(contenu, origineImages).split(LIGNES_SIGNATURE)) {
    if (morceau === '<br>' && sauterRetour) { sauterRetour = false; continue; }
    sauterRetour = false;
    const vides = [...morceau.matchAll(VARIABLE)].some(([, v]) => estVariable(v!) && !valeurs[v]?.trim());
    if (vides) { sauterRetour = true; continue; }
    sortie.push(morceau.replace(VARIABLE, (tout, v: string) => (estVariable(v) ? echapperValeur(valeurs[v]!.trim()) : tout)));
  }
  // Une ligne retirée peut emporter l'ouverture d'une balise (gras…) : la sortie est remise d'aplomb.
  return nettoyerHtmlRedaction(sortie.join(''), origineImages);
}

/**
 * Images AGORA d'une signature (web/public/signature, PNG à 2× pour les écrans denses) : logo PRODEVAL sans slogan
 * (600 × 86, affiché en 300 × 43) et icônes rondes des réseaux sociaux (48 × 48, affichées en 24 × 24).
 */
export const IMAGES_SIGNATURE = ['logo-prodeval', 'facebook', 'linkedin', 'youtube', 'x'] as const;
export const cheminImageSignature = (nom: (typeof IMAGES_SIGNATURE)[number]) => `/signature/${nom}.png`;

const reseau = (lien: string, image: (typeof IMAGES_SIGNATURE)[number], nom: string) =>
  `<a href="${lien}"><img src="${cheminImageSignature(image)}" width="24" height="24" alt="${nom}" style="border: 0"></a>`;

/**
 * Modèle de signature PRODEVAL (format du marketing, 28/10/2026) : nom, fonction et mobile de l'utilisateur, logo,
 * adresse du siège (FR01, Châteauneuf-sur-Isère), standard, site et réseaux sociaux. Point de départ proposé dans
 * l'administration des signatures ; adresse et standard à adapter pour une autre filiale.
 */
export const MODELE_SIGNATURE_PRODEVAL = [
  '<div style="font-family: Calibri, Arial, sans-serif; font-size: 11pt; color: #1C464C; line-height: 1.4">',
  '<b>{{nomComplet}}</b><br>',
  '{{fonction}}<br>',
  '{{mobile}}<br>',
  '<br>',
  `<img src="${cheminImageSignature('logo-prodeval')}" width="300" height="43" alt="PRODEVAL"><br>`,
  '<br>',
  '<span style="font-size: 9pt">7 rue Anne-Marie Staub, Quartier du 45ème parallèle</span><br>',
  '<span style="font-size: 9pt">26300 Chateauneuf-sur-Isère | BP 22145 - VALENCE CEDEX 9</span><br>',
  '<span style="font-size: 9pt">+33(0)4 75 40 37 37</span><br>',
  '<a href="https://www.prodeval.com" style="color: #81BB27">www.prodeval.com</a><br>',
  [
    reseau('https://www.facebook.com/ProdevalBiogas/', 'facebook', 'Facebook'),
    reseau('https://www.linkedin.com/company/prodeval/', 'linkedin', 'LinkedIn'),
    reseau('https://www.youtube.com/channel/UCUhvlfoAyrq5COiwEm-mv2A', 'youtube', 'YouTube'),
    reseau('https://twitter.com/Prodeval_biogas', 'x', 'X'),
  ].join(' '),
  '</div>',
].join('\n');

/**
 * Pièces jointes ajoutées depuis AGORA à un brouillon (nouveau message, réponse, transfert) : 3 Mo au plus par fichier
 * (dépôt simple de Graph ; au-delà, joindre depuis Outlook), 10 fichiers par envoi, exécutables et scripts refusés.
 */
export const TAILLE_MAX_PIECE_ENVOI = 3 * 1024 * 1024;
export const MAX_PIECES_ENVOI = 10;
export const EXTENSIONS_BLOQUEES = [
  'exe', 'com', 'bat', 'cmd', 'scr', 'pif', 'msi', 'msp', 'jar', 'js', 'jse', 'vbs', 'vbe', 'wsf', 'wsh', 'ps1',
  'hta', 'cpl', 'lnk', 'reg', 'scf', 'dll', 'iso', 'img',
] as const;
export const extensionBloquee = (nom: string) =>
  (EXTENSIONS_BLOQUEES as readonly string[]).includes((nom.includes('.') ? nom.split('.').pop()! : '').toLowerCase());

/** Adresses saisies dans un champ (séparées par des virgules, points-virgules ou espaces) → liste. */
export function separerAdresses(v: unknown): unknown {
  if (Array.isArray(v)) return v;
  if (typeof v !== 'string') return v;
  return v.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
}

/**
 * Texte collé ou saisi dans un champ de destinataires (« Jean Dupont <jean@exemple.fr>; b@exemple.fr ») → adresses,
 * dans l'ordre, sans doublon. Une partie sans « @ » reste entière (nom tapé sans choisir de suggestion : signalé en
 * erreur par le schéma plutôt qu'oublié).
 */
export function extraireAdresses(texte: string): string[] {
  const vues = new Map<string, string>();
  for (const partie of texte.split(/[,;\n]+/)) {
    const entre = /<([^<>]*)>/.exec(partie);
    const brut = (entre ? entre[1]! : partie).trim().replace(/^mailto:/i, '');
    for (const a of !entre && brut.includes('@') ? brut.split(/\s+/) : [brut]) {
      if (a && !vues.has(a.toLowerCase())) vues.set(a.toLowerCase(), a);
    }
  }
  return [...vues.values()];
}

/** Suggestions de destinataires renvoyées pour une saisie. */
export const MAX_SUGGESTIONS_DESTINATAIRES = 10;
const plier = (v: string) => v.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

/**
 * Suggestions de destinataires (API et maquette) : candidats dont le nom, l'adresse ou l'entreprise contient la saisie
 * (sans accents ni casse) ; ceux dont un mot COMMENCE par la saisie d'abord, puis l'ordre reçu (collègues, contacts) ;
 * une adresse une seule fois ; MAX_SUGGESTIONS_DESTINATAIRES au plus.
 */
export function classerDestinataires<T extends { nom: string; adresse: string; detail?: string | null }>(q: string, candidats: T[]): T[] {
  const saisie = plier(q.trim());
  const vues = new Set<string>();
  return candidats
    .map((d, i) => ({ d, i, texte: plier(`${d.nom} ${d.adresse} ${d.detail ?? ''}`) }))
    .filter((x) => x.texte.includes(saisie))
    .map((x) => ({ ...x, rang: ` ${x.texte.replace(/[@.,'’-]/g, ' ')}`.includes(` ${saisie}`) ? x.i : candidats.length + x.i }))
    .sort((a, b) => a.rang - b.rang)
    .map((x) => x.d)
    .filter((d) => { const a = d.adresse.toLowerCase(); if (vues.has(a)) return false; vues.add(a); return true; })
    .slice(0, MAX_SUGGESTIONS_DESTINATAIRES);
}

/**
 * Nom du fichier .eml d'un e-mail enregistré dans AGORA (lot 2, flux) : son objet sans les caractères interdits dans
 * un nom de fichier, 150 caractères au plus ; « e-mail.eml » sans objet. API et maquette.
 */
export function nomFichierEml(objet: string | null | undefined): string {
  const base = (objet ?? '')
    .replace(/[\\/:*?"<>|\u0000-\u001f\u007f]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 150)
    .replace(/[. ]+$/, '');
  return `${base || 'e-mail'}.eml`;
}
